import maplibregl from 'maplibre-gl';
import { useEffect, useRef } from 'react';
import { useMap } from './map-view';

/** Click the map to place the point; drag the marker to move it. */
export function PointPicker({
  value,
  onChange,
  color = '#0b3f82',
  icon,
  readOnly = false,
}: {
  value?: [number, number] | null;
  onChange: (p: [number, number]) => void;
  /** Shows the point without letting it be moved. */
  readOnly?: boolean;
  color?: string;
  /** Image URL for the marker instead of the default pin (centred on the point). */
  icon?: string;
}) {
  const map = useMap();
  const marker = useRef<maplibregl.Marker | null>(null);
  const change = useRef(onChange);
  change.current = onChange;

  useEffect(() => {
    if (!map) return;
    if (readOnly) {
      return () => {
        marker.current?.remove();
        marker.current = null;
      };
    }
    const round = (n: number) => Math.round(n * 1e7) / 1e7;
    const click = (e: maplibregl.MapMouseEvent) => change.current([round(e.lngLat.lng), round(e.lngLat.lat)]);
    map.on('click', click);
    map.getCanvas().style.cursor = 'crosshair';
    return () => {
      map.off('click', click);
      map.getCanvas().style.cursor = '';
      marker.current?.remove();
      marker.current = null;
    };
  }, [map, readOnly]);

  useEffect(() => {
    if (!map) return;
    if (!value) {
      marker.current?.remove();
      marker.current = null;
      return;
    }
    if (!marker.current) {
      let element: HTMLElement | undefined;
      if (icon) {
        element = document.createElement('img');
        (element as HTMLImageElement).src = icon;
        element.style.width = '34px';
        element.style.height = '34px';
        element.style.cursor = readOnly ? 'default' : 'grab';
      }
      marker.current = new maplibregl.Marker(
        element ? { draggable: !readOnly, element } : { draggable: !readOnly, color },
      )
        .setLngLat(value)
        .addTo(map);
      marker.current.on('dragend', () => {
        const { lng, lat } = marker.current!.getLngLat();
        change.current([Math.round(lng * 1e7) / 1e7, Math.round(lat * 1e7) / 1e7]);
      });
    } else {
      marker.current.setLngLat(value);
    }
  }, [map, value, color, icon, readOnly]);

  return null;
}
